import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { shopApi, apiError, setBuyer, getBuyer } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CircleNotch, Package, SignOut, ArrowLeft, CheckCircle, Clock, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const statusMeta = {
  paid: { key: "paid", icon: CheckCircle, cls: "text-emerald-400" },
  pending: { key: "pending", icon: Clock, cls: "text-gold" },
  failed: { key: "failed", icon: XCircle, cls: "text-red-400" },
  error: { key: "failed", icon: XCircle, cls: "text-red-400" },
};

export default function ShopOrders() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const [buyer, setBuyerState] = useState(getBuyer());
  const [orders, setOrders] = useState(null);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const loadOrders = () => shopApi.get("/shop/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { if (buyer) loadOrders(); }, [buyer]);

  const submit = async () => {
    setBusy(true);
    try {
      const path = mode === "register" ? "/shop/register" : "/shop/login";
      const { data } = await shopApi.post(path, form);
      setBuyer(data.token, data.buyer);
      setBuyerState(data.buyer);
      toast.success(mode === "register" ? "Cuenta creada" : "Sesión iniciada");    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const logout = () => { setBuyer(null); setBuyerState(null); setOrders(null); };

  if (!buyer) {
    return (
      <div className="min-h-screen bg-[#0B0B0F] text-neutral-100 flex items-center justify-center px-6" data-testid="shop-auth-page">
        <LanguageSwitcher className="fixed bottom-4 right-4 z-30" />
        <div className="w-full max-w-sm">
          <Link to={`/tienda/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-gold mb-6"><ArrowLeft size={15} /> {t("common.backToStore")}</Link>
          <h1 className="text-2xl font-bold mb-1">{mode === "register" ? t("orders.createAccount") : t("orders.myOrdersTitle")}</h1>
          <p className="text-neutral-400 text-sm mb-6">{mode === "register" ? t("orders.registerSub") : t("orders.loginSub")}</p>
          <div className="space-y-3">
            {mode === "register" && <Input placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/15" data-testid="shop-name-input" />}
            <Input placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/15" data-testid="shop-email-input" />
            <Input type="password" placeholder={t("common.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white/5 border-white/15" data-testid="shop-password-input" />
            <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="shop-submit-btn">
              {busy ? <CircleNotch className="animate-spin" /> : mode === "register" ? t("orders.createBtn") : t("orders.enter")}
            </Button>
          </div>
          <button onClick={() => setMode(mode === "register" ? "login" : "register")} className="text-sm text-gold hover:underline mt-5 block mx-auto" data-testid="shop-toggle-mode">
            {mode === "register" ? t("orders.haveAccount") : t("orders.noAccount")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-neutral-100" data-testid="shop-orders-page">
      <LanguageSwitcher className="fixed bottom-4 right-4 z-30" />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to={`/tienda/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-gold mb-2"><ArrowLeft size={15} /> {t("common.backToStore")}</Link>
            <h1 className="text-3xl font-bold tracking-tight">{t("orders.myOrdersTitle")}</h1>
            <p className="text-neutral-500 text-sm mt-1">{buyer.email}</p>
          </div>
          <button onClick={logout} className="text-neutral-400 hover:text-red-400 flex items-center gap-1.5 text-sm" data-testid="shop-logout-btn"><SignOut size={16} /> {t("orders.logout")}</button>
        </div>

        {orders === null ? <CircleNotch className="animate-spin text-gold" size={26} />
          : orders.length === 0 ? (
            <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center text-neutral-500" data-testid="shop-orders-empty">{t("orders.empty")}</div>
          ) : (
            <div className="space-y-3" data-testid="shop-orders-list">
              {orders.map((o) => {
                const s = statusMeta[o.status] || statusMeta.pending;
                const Icon = s.icon;
                return (
                  <div key={o.id} className="border border-white/10 rounded-xl p-5 bg-white/[0.03]" data-testid={`shop-order-${o.id}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-serif text-lg">${Number(o.amount).toLocaleString()}</span>
                      <span className={`inline-flex items-center gap-1.5 text-sm ${s.cls}`}><Icon size={16} weight="fill" /> {t(`orders.status.${s.key}`)}</span>
                    </div>
                    <div className="text-sm text-neutral-400 mt-3 space-y-0.5">
                      {(o.items || []).map((it, i) => <div key={i}><Package size={13} className="inline mr-1 text-neutral-600" />{it.name} ×{it.quantity}{it.customization_text ? ` — ${it.customization_text}` : ""}</div>)}
                    </div>
                    {o.created_at && <div className="text-[11px] text-neutral-600 mt-2">{new Date(o.created_at).toLocaleString()}</div>}
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}
